<script lang="ts" setup>
import type { CSSProperties } from 'vue'
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

defineProps<{
  infoText: string
  tooltipText?: string
}>()

const showTooltip = ref(false)
const wrapperRef = ref<HTMLElement | null>(null)
const tooltipRef = ref<HTMLElement | null>(null)
const tooltipStyle = ref<CSSProperties>({
  top: '0px',
  left: '0px',
  position: 'fixed',
  zIndex: '1000',
})

const toggleTooltip = () => {
  showTooltip.value = !showTooltip.value
}

const updateTooltipPosition = () => {
  if (!wrapperRef.value || !tooltipRef.value) return

  const rect = wrapperRef.value.getBoundingClientRect()
  const tooltip = tooltipRef.value

  const tooltipWidth = tooltip.offsetWidth
  const tooltipHeight = tooltip.offsetHeight
  const padding = 8

  let top = rect.top - tooltipHeight - padding
  let left = rect.left + rect.width / 2 - tooltipWidth / 2

  const maxLeft = window.innerWidth - tooltipWidth - padding

  if (left < padding) left = padding
  if (left > maxLeft) left = maxLeft
  if (top < padding) top = rect.bottom + padding

  tooltipStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
    position: 'fixed',
    zIndex: '1000',
  }
}

watch(showTooltip, async (val) => {
  if (val) {
    await nextTick()
    updateTooltipPosition()
  }
})

onMounted(() => {
  window.addEventListener('resize', updateTooltipPosition)
  window.addEventListener('scroll', updateTooltipPosition, true)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateTooltipPosition)
  window.removeEventListener('scroll', updateTooltipPosition, true)
})

</script>

<template>
  <div class="info-badge">
    {{ infoText }}
    <div ref="wrapperRef" class="tooltip-wrapper">
      <i
        class="ic-info-circle"
        @click.prevent.stop="toggleTooltip"
        @mouseenter="showTooltip = true"
        @mouseleave="showTooltip = false"
      />
    </div>

    <Teleport to=".container.main">
      <div
        v-if="tooltipText && showTooltip"
        ref="tooltipRef"
        class="tooltip"
        :style="tooltipStyle"
      >
        {{ tooltipText }}
        <div class="tooltip-arrow" />
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.info-badge {
  position: relative;
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;

  gap: 0.375rem;
  padding: 0.25rem 0.75rem;

  border-radius: 1.25rem;
  border: 1px solid $borderColor;
  background: $borderColor;
  color: $textColor;

  text-align: center;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.125rem;
  word-break: break-word;
  max-width: 100%;

  @include media('>=tablet') {
    width: fit-content;
  }

  .tooltip-wrapper {
    position: relative;
    display: flex;
    overflow: visible;
  }
}

.tooltip {
  position: fixed;
  color: $textColorWhite;
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  line-height: 1.2;
  overflow-wrap: break-word;
  white-space: normal;
  width: max-content;
  max-width: 240px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

  .theme-va & {
    background-color: $vaPrimaryDarkColor;
  }

  .theme-sd & {
    background-color: $sdPrimaryDarkColor;
  }
}

.tooltip-arrow {
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;

  .theme-va & {
    border-top: 6px solid $vaPrimaryDarkColor;
  }

  .theme-sd & {
    border-top: 6px solid $sdPrimaryDarkColor;
  }
}
</style>
